import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError, pickImage } from "@/lib/api";
import { optimizeImageToWebp } from "@/lib/image";
import { useSite } from "@/context/SiteContext";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Header } from "./IndustriesPanel";
import BoxAnnotator from "./BoxAnnotator";

const BLANK = {
  title: "",
  description: "",
  image_url: "",
  image_data: null,
  cam_id: "CAM-01",
  accent: "lime",
  stat_label: "",
  stat_value: "",
  boxes: [],
  category_id: "",
  industry_ids: [],
  tag_ids: [],
  order: 100,
};

export default function FeatureEditorPage() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const site = useSite();
  const [form, setForm] = useState(BLANK);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [optimizing, setOptimizing] = useState(false);

  useEffect(() => {
    if (isNew) {
      setForm((f) => ({
        ...BLANK,
        category_id: site.categories[0]?.id || "",
      }));
      return;
    }
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/admin/features/${id}`);
        setForm({ ...BLANK, ...data });
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isNew, site.categories]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleImageUpload = async (file) => {
    if (!file) return;
    // Allow larger source files — we convert to high-quality WebP client-side.
    // Hard cap source to avoid memory issues (e.g. multi-megapixel phone photos).
    if (file.size > 25 * 1024 * 1024) {
      toast.error("Image is too large (max 25 MB before optimization).");
      return;
    }
    setOptimizing(true);
    try {
      const webpDataUrl = await optimizeImageToWebp(file, {
        maxBytes: 2 * 1024 * 1024,
        quality: 0.95,
      });
      update("image_data", webpDataUrl);
      update("image_url", "");
      toast.success("Image optimized to WebP. Drag on the image to draw boxes.");
    } catch (e) {
      toast.error(e?.message || "Could not optimize image.");
    } finally {
      setOptimizing(false);
    }
  };

  const save = async () => {
    if (!form.title.trim()) return toast.error("Title is required.");
    if (!form.category_id) return toast.error("Pick a category.");
    setSaving(true);
    try {
      const payload = {
        ...form,
        order: Number(form.order) || 100,
      };
      if (isNew) {
        const { data } = await api.post("/admin/features", payload);
        toast.success("Feature created.");
        await site.reload();
        navigate(`/admin/features/${data.id}`, { replace: true });
      } else {
        await api.patch(`/admin/features/${id}`, payload);
        toast.success("Saved.");
        await site.reload();
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this feature permanently?")) return;
    try {
      await api.delete(`/admin/features/${id}`);
      toast.success("Deleted.");
      await site.reload();
      navigate("/admin/features", { replace: true });
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleId = (key, value) => {
    const list = form[key] || [];
    const next = list.includes(value) ? list.filter((x) => x !== value) : [...list, value];
    update(key, next);
  };

  if (loading) return <p className="text-sm text-neutral-500">Loading…</p>;

  return (
    <div data-testid="feature-editor" className="space-y-6 pb-20">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <Link to="/admin/features" className="text-xs text-neutral-500 hover:text-neutral-900">
            ← All features
          </Link>
          <Header title={isNew ? "New feature" : form.title || "Edit feature"} />
        </div>
        <div className="flex items-center gap-2">
          {!isNew && (
            <button
              type="button"
              data-testid="feature-delete"
              onClick={remove}
              className="text-xs font-semibold text-red-600 hover:text-red-700 px-3 py-2 rounded-md border border-red-200 hover:bg-red-50"
            >
              Delete
            </button>
          )}
          <Button
            type="button"
            data-testid="feature-save"
            disabled={saving}
            onClick={save}
            className="bg-[#84CC16] text-[#0A0A0A] hover:bg-[#65A30D] font-semibold"
          >
            {saving ? "Saving…" : "Save feature"}
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-12 gap-6">
        {/* Left: image + annotator */}
        <div className="lg:col-span-7 space-y-4">
          <div className="bg-white border border-neutral-200 rounded-xl p-4 space-y-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <span className="font-mono-ui text-[10px] uppercase tracking-[0.18em] text-neutral-500">
                Snapshot image
              </span>
              <div className="flex items-center gap-2">
                <label
                  className={`text-xs font-semibold px-3 py-1.5 rounded-md bg-[#0A0A0A] text-white cursor-pointer hover:bg-neutral-800 ${
                    optimizing ? "opacity-60 pointer-events-none" : ""
                  }`}
                  data-testid="feature-image-upload-label"
                >
                  {optimizing ? "Optimizing…" : "Upload image"}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif,image/bmp"
                    className="hidden"
                    disabled={optimizing}
                    data-testid="feature-image-upload-input"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      e.target.value = "";
                      handleImageUpload(f);
                    }}
                  />
                </label>
                {(form.image_data || form.image_url) && (
                  <button
                    type="button"
                    data-testid="feature-image-clear"
                    onClick={() => {
                      update("image_data", null);
                      update("image_url", "");
                    }}
                    className="text-xs font-semibold text-neutral-700 px-3 py-1.5 rounded-md border border-neutral-200"
                  >
                    Clear image
                  </button>
                )}
              </div>
            </div>
            <div>
              <span className="block font-mono-ui text-[10px] uppercase tracking-[0.18em] text-neutral-500 mb-1">
                Image URL (used if no upload)
              </span>
              <Input
                data-testid="feature-input-image-url"
                value={form.image_url || ""}
                onChange={(e) => {
                  update("image_url", e.target.value);
                  update("image_data", null);
                }}
                placeholder="https://…"
                disabled={Boolean(form.image_data)}
              />
            </div>
            <BoxAnnotator
              src={pickImage(form)}
              boxes={form.boxes || []}
              onChange={(b) => update("boxes", b)}
              camId={form.cam_id}
            />
          </div>
        </div>

        {/* Right: metadata */}
        <div className="lg:col-span-5 space-y-4">
          <div className="bg-white border border-neutral-200 rounded-xl p-4 space-y-3">
            <Field label="Title *">
              <Input
                data-testid="feature-input-title"
                value={form.title}
                onChange={(e) => update("title", e.target.value)}
              />
            </Field>
            <Field label="Description">
              <Textarea
                data-testid="feature-input-description"
                value={form.description}
                onChange={(e) => update("description", e.target.value)}
                rows={4}
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Camera ID">
                <Input
                  data-testid="feature-input-cam-id"
                  value={form.cam_id}
                  onChange={(e) => update("cam_id", e.target.value)}
                />
              </Field>
              <Field label="Order">
                <Input
                  data-testid="feature-input-order"
                  type="number"
                  value={form.order}
                  onChange={(e) => update("order", e.target.value)}
                />
              </Field>
              <Field label="Stat label">
                <Input
                  data-testid="feature-input-stat-label"
                  value={form.stat_label || ""}
                  onChange={(e) => update("stat_label", e.target.value)}
                />
              </Field>
              <Field label="Stat value">
                <Input
                  data-testid="feature-input-stat-value"
                  value={form.stat_value || ""}
                  onChange={(e) => update("stat_value", e.target.value)}
                />
              </Field>
            </div>
            <Field label="Category *">
              <Select
                value={form.category_id}
                onValueChange={(v) => update("category_id", v)}
              >
                <SelectTrigger data-testid="feature-select-category"><SelectValue placeholder="Pick a category" /></SelectTrigger>
                <SelectContent>
                  {site.categories.map((c) => (
                    <SelectItem key={c.id} value={c.id} data-testid={`feature-category-option-${c.slug}`}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Industries">
              <div className="flex flex-wrap gap-1.5">
                {site.industries.map((i) => {
                  const active = form.industry_ids.includes(i.id);
                  return (
                    <button
                      key={i.id}
                      type="button"
                      data-testid={`feature-industry-${i.slug}`}
                      onClick={() => toggleId("industry_ids", i.id)}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-all ${
                        active
                          ? "bg-[#0A0A0A] border-[#0A0A0A] text-white"
                          : "bg-white border-neutral-200 text-neutral-700 hover:border-neutral-900"
                      }`}
                    >
                      {i.name}
                    </button>
                  );
                })}
              </div>
            </Field>
            <Field label="Tags">
              <TagInput
                value={form.tag_ids}
                onChange={(ids) => update("tag_ids", ids)}
                site={site}
              />
            </Field>
          </div>
        </div>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <span className="block font-mono-ui text-[10px] uppercase tracking-[0.18em] text-neutral-500 mb-1">
      {label}
    </span>
    {children}
  </label>
);

// Tag input - chips for selected tags + comma-separated input.
// Typing "ANPR, OCR, License Plate" + Enter (or comma, or blur) attaches existing
// tags by case-insensitive name match and creates any that don't exist yet.
const TagInput = ({ value, onChange, site }) => {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  // Tags created in this session - used to render chips immediately, before
  // /api/site/bootstrap finishes refreshing.
  const [pendingTags, setPendingTags] = useState([]);

  const knownTags = [...(site.tags || []), ...pendingTags];
  const tagsById = Object.fromEntries(knownTags.map((t) => [t.id, t]));
  const tagsByLowerName = Object.fromEntries(knownTags.map((t) => [t.name.trim().toLowerCase(), t]));

  const commit = async () => {
    const raw = text.trim().replace(/,$/, "").trim();
    if (!raw) {
      setText("");
      return;
    }
    const names = Array.from(
      new Set(
        raw
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
      )
    );
    if (names.length === 0) {
      setText("");
      return;
    }
    setBusy(true);
    const nextIds = [...value];
    const newlyCreated = [];
    try {
      for (const name of names) {
        const existing = tagsByLowerName[name.toLowerCase()];
        let id;
        if (existing) {
          id = existing.id;
        } else {
          const { data } = await api.post("/admin/tags", { name });
          id = data.id;
          newlyCreated.push(data);
          tagsByLowerName[name.toLowerCase()] = data;
        }
        if (!nextIds.includes(id)) nextIds.push(id);
      }
      if (newlyCreated.length > 0) {
        setPendingTags((cur) => [...cur, ...newlyCreated]);
      }
      onChange(nextIds);
      setText("");
      // Refresh global site context in the background so other parts of the app
      // see the new tags too.
      site.reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const removeTag = (id) => onChange(value.filter((x) => x !== id));

  return (
    <div data-testid="feature-tag-input">
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {value.map((id) => {
            const t = tagsById[id];
            if (!t) return null;
            return (
              <span
                key={id}
                data-testid={`feature-tag-chip-${t.slug || id}`}
                className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1 rounded-full bg-[#84CC16] text-neutral-900 border border-[#84CC16]"
              >
                {t.name}
                <button
                  type="button"
                  onClick={() => removeTag(id)}
                  className="text-neutral-900/70 hover:text-neutral-900"
                  aria-label={`Remove ${t.name}`}
                >
                  ×
                </button>
              </span>
            );
          })}
        </div>
      )}
      <Input
        data-testid="feature-tag-input-text"
        value={text}
        onChange={(e) => {
          const v = e.target.value;
          // If user typed a comma, treat that as the separator and commit
          if (v.endsWith(",")) {
            setText(v);
            // Use a microtask so the visual update happens first
            setTimeout(() => commit(), 0);
          } else {
            setText(v);
          }
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit();
          } else if (e.key === "Backspace" && text === "" && value.length > 0) {
            removeTag(value[value.length - 1]);
          }
        }}
        onBlur={commit}
        disabled={busy}
        placeholder="ANPR, OCR, License Plate (comma-separated · Enter to add)"
      />
      <p className="mt-1 text-[11px] text-neutral-500">
        Existing tags are reused; new names are created on save and become searchable on the public site.
      </p>
    </div>
  );
};
